import { useContext, useEffect, useRef } from 'react';
import { useIdentity } from '@vp/auth-react';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { IIdentityObject } from '../../interfaces/app-interfaces';
import { API_ENDPOINTS } from '../../config/api-config';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { sharedWebSocketRef } from '../../utils/websocket-singleton';
import { CAContext } from '../../contexts/CAContext';
import { encryptAES } from '../../utils/encryptAES';
import { getEnvConfig } from '../../utils/env-utils';

interface UseChatFooterWebSocketOptions {
  conversationId: string;
  isrequestConnection?: boolean;
}

export function useChatFooterWebSocket({ conversationId, isrequestConnection }: UseChatFooterWebSocketOptions) {
  const {
    state: { enableSneakPeak },
  } = useContext(MiawChatContext);

  const {
    chatState: { chatSession },
  } = useContext(CAContext);

  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();
  const wsRef = sharedWebSocketRef;
  const { authorizationHeader } = identity as IIdentityObject;

  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTypedSomethingRef = useRef(false);

  useEffect(() => {
    if (!conversationId || !enableSneakPeak || !authorizationHeader || !isrequestConnection) {
      if (chatSession !== 'miawChatSession' || !enableSneakPeak) return;

      if (!isrequestConnection) {
        console.log('[WebSocket] Connection not requested by component');
        logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
          logLevel: 'info',
          message: `Connection not requested by component. conversationId = ${conversationId}`,
          apiType: 'webSocketConnection',
        });
      } else {
        console.log('[WebSocket] Missing required params:', { conversationId, enableSneakPeak, hasAuth: !!authorizationHeader });
        if (!conversationId || !authorizationHeader)
          logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
            logLevel: 'error',
            message: `Missing required params for websocket connection. Connection not established. conversationId = ${conversationId}, hasAuth = ${!!authorizationHeader}, isenableSneakPeak = ${enableSneakPeak}`,
            apiType: 'webSocketConnection',
          });
      }
      return;
    }

    if (wsRef.current) {
      console.log('[WebSocket] Closing existing connection before creating new one');
      wsRef.current.close(1000, 'Closing existing connection before creating new one');
    }

    logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
      logLevel: 'info',
      message: `Attempting to establish websocket connection. conversationId = ${conversationId}`,
      apiType: 'webSocketConnection',
    });

    const ws = new WebSocket(`${API_ENDPOINTS.SNEAK_PEAK_WS_URL()}/?conversationId=${conversationId}&authorization=${authorizationHeader}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket] connection opened');

      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'info',
        message: `WebSocket Connected successfully. conversationId = ${conversationId}`,
        apiType: 'webSocketConnection',
      });
    };

    ws.onclose = (event) => {
      console.log('[WebSocket] connection closed:', event.code, event.reason);
      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: event.code === 1000 ? 'info' : 'error',
        message: `WebSocket connection closed, conversationId = ${conversationId}, reason = ${event?.reason}, code = ${event?.code}`,
        apiType: 'webSocketConnection',
      });
    };

    ws.onerror = (err) => {
      console.log('[WebSocket] error occurred', err);

      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'error',
        message: `WebSocket error occurred. conversationId = ${conversationId}, error = ${String(err)}`,
        apiType: 'webSocketConnection',
      });
    };
  }, [conversationId]);

  const sendEncrypted = async (message) => {
    const key = getEnvConfig().sneakPeakSecret || '';
    try {
      const encrypted = await encryptAES(message, atob(key));
      return encrypted;
    } catch (error) {
      logCustomAttributesNR('ca-chatbot-miaw-encrypt', {
        logLevel: 'error',
        message: `Encryption failed ${JSON.stringify(error)}`,
        apiType: 'miawRetrieveConversation',
      });
      console.error('Error encrypting message for WebSocket:', error);
      return undefined;
    }
  };

  const sendWebSocketMessage = async (text: string) => {
    if (!enableSneakPeak) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const encryptAESedMessage = await sendEncrypted(text);

      wsRef.current.send(
        JSON.stringify({
          type: 'message_change',
          action: 'message',
          conversationId,
          data: encryptAESedMessage,
        }),
      );
      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'info',
        message: `Sent message over WebSocket. conversationId = ${conversationId}, textLength = ${text?.length}`,
        apiType: 'webSocketConnection',
      });
    } else {
      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'error',
        message: `Unable to send message — WebSocket not open. conversationId = ${conversationId}, wsReadyState = ${wsRef.current?.readyState}`,
        apiType: 'webSocketConnection',
      });
    }
  };

  const handleTyping = (message: string) => {
    if (!enableSneakPeak) return;
    const lastChar = message.slice(-1);
    const prevChar = message.slice(-2, -1);
    const triggerChars = [' ', ',', '.', '!'];

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    if (/\S/.test(message)) hasTypedSomethingRef.current = true;

    if (triggerChars.includes(lastChar)) {
      if (lastChar === ' ' && prevChar === ' ') return;
      if (hasTypedSomethingRef.current) {
        sendWebSocketMessage(message);
      }
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (lastChar !== ' ') sendWebSocketMessage(message);
    }, 1500);
  };

  const closeConnection = (reason: string = 'Manually closed by app') => {
    const ws = wsRef.current;
    console.log('[WebSocket] closeConnection', ws, ws?.readyState, wsRef);
    if (ws && ws.readyState === WebSocket.OPEN) {
      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'info',
        message: `Closing WebSocket connection manually. conversationId = ${conversationId}, reason = ${reason}`,
        apiType: 'webSocketConnection',
      });
      ws.close(1000, reason);
      wsRef.current = null;
    } else {
      logCustomAttributesNR('ca-chatbot-miaw-sneak-peak', {
        logLevel: 'info',
        message: `closeConnection: WebSocket already closed or not open. conversationId = ${conversationId}, wsReadyState = ${ws?.readyState}`,
        apiType: 'webSocketConnection',
      });
    }
  };

  return { handleTyping, closeConnection };
}
